<template>
    <div class="session__edit_cancellations">
        <form-checkbox
            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
            :label="$t('ebs.sessions.edit.labels.learnersCanCancel')"
            :name="'cancel-booking-availability'"
            v-model="sessionCancellationSelfAllowed"
        />
        <transition name="fade" mode="out-in">
            <form-number-radio-horizontal
                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                v-if="sessionCancellationSelfAllowed"
                :label="$t('ebs.sessions.edit.labels.allowBooking')"
                :after="$t('ebs.sessions.edit.labels.beforeStart')"
                :name="'cancel-booking-availability-before'"
                :items="[
                    {
                        name: this.$t('ebs.sessions.edit.name.hours'),
                        value: 'hour'
                    },
                    {
                        name: this.$t('ebs.sessions.edit.name.days'),
                        value: 'day'
                    }
                ]"
                :v-validate="'min_value:0'"
                :value-number="sessionCancellationSelfLimit"
                :value-radio="sessionCancellationSelfLimitType"
                @emit-value-number="updateSessionCancellationSelfLimit"
                @emit-value-radio="updateSessionCancellationSelfLimitType"
            />
        </transition>
        <component-title :size="'medium'">{{ $t('ebs.sessions.edit.labels.cancellationReason') }}</component-title>
        <form-checkbox
            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
            :label="$t('ebs.sessions.edit.labels.askReason')"
            :name="'cancel-booking-reason'"
            :hint="$t('ebs.sessions.edit.text.askLearners')"
            v-model="sessionCancellationReasonRequired"
        />
        <transition-group name="fade" mode="out-in">
            <form-textarea
                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                :key="'cancel-booking-reason-items'"
                v-if="sessionCancellationReasonRequired"
                :label="$t('ebs.sessions.edit.labels.cancellationReason')"
                :name="'cancel-booking-reason-items'"
                :required="true"
                :data-vv-as="$t('forms.dataVvAs.cancellationReason')"
                :hint="$t('ebs.sessions.edit.text.addANewReason')"
                :v-validate="'required'"
                v-model="sessionCancellationReason"
            >{{ $t('ebs.sessions.edit.text.addANewReason') }}
            </form-textarea>
            <form-checkbox
                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                :key="'allow-errors-reasons'"
                v-if="sessionCancellationReasonRequired"
                :name="'allow-errors-reasons'"
                :label="$t('ebs.sessions.edit.labels.allowOther')"
                v-model="sessionCancellationReasonOther"
            />
        </transition-group>
        <component-title :size="'medium'">{{ $t('ebs.sessions.edit.title.automaticCancellations') }}</component-title>
        <form-checkbox
            :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
            :label="$t('ebs.sessions.edit.labels.automaticallyCancel')"
            :name="'cancel-booking-automatic'"
            v-model="sessionCancellationAutoEnabled"
        />
        <transition name="fade" mode="out-in">
            <form-number-radio-horizontal
                :disabled="isEditMode && (!isSessionUpcoming && !isSessionInProgress && !isSessionDraft)"
                v-if="sessionCancellationAutoEnabled"
                :label="''"
                :after="$t('ebs.sessions.edit.text.beforeStart')"
                :name="'cancel-booking-automatic-before'"
                :items="[
                    {
                        name: this.$t('ebs.sessions.edit.name.hours'),
                        value: 'hour'
                    },
                    {
                        name: this.$t('ebs.sessions.edit.name.days'),
                        value: 'day'
                    }
                ]"
                :v-validate="'min_value:0'"
                :value-number="sessionCancellationAutoLimit"
                :value-radio="sessionCancellationAutoLimitType"
                @emit-value-number="updateSessionCancellationAutoLimit"
                @emit-value-radio="updateSessionCancellationAutoLimitType"
            />
        </transition>
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormNumberRadioHorizontal from '@/components/default/forms/FormNumberRadioHorizontal'
import FormTextarea from '@/components/default/forms/FormTextarea'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import { performReset, performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'

export default {
  components: {
    ComponentTitle,
    PopNotify,
    FormTextarea,
    FormNumberRadioHorizontal,
    FormCheckbox
  },
  name: 'ebs-session-edit-cancellations',
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit],
  computed: {
    rootItemName () {
      return 'session'
    },
    isEditMode () {
      return this.$route.name.includes('session-edit')
    },
    isSessionDraft () {
      return this.sessionStatus === 'draft'
    },
    isSessionUpcoming () {
      return this.sessionStatus === 'upcoming'
    },
    isSessionInProgress () {
      return this.sessionStatus === 'in_progress'
    },
    sessionStatus () {
      return this.$store.getters.getSessionDefaultStatus
    },
    sessionCancellationSelfAllowed: {
      get () {
        return this.$store.getters['getSessionCancellationSelfAllowed']
      },
      set (value) {
        this.$store.dispatch('setSessionCancellationSelfAllowed', value)
      }
    },
    sessionCancellationSelfLimit: {
      get () {
        return this.$store.getters['getSessionCancellationSelfLimit']
      }
    },
    sessionCancellationSelfLimitType: {
      get () {
        return this.$store.getters['getSessionCancellationSelfLimitType']
      }
    },
    sessionCancellationReasonRequired: {
      get () {
        return this.$store.getters['getSessionCancellationReasonRequired']
      },
      set (value) {
        this.$store.dispatch('setSessionCancellationReasonRequired', value)
      }
    },
    sessionCancellationReason: {
      get () {
        return this.$store.getters['getSessionCancellationReason']
      },
      set (value) {
        this.$store.dispatch('setSessionCancellationReason', value)
      }
    },
    sessionCancellationReasonOther: {
      get () {
        return this.$store.getters['getSessionCancellationReasonOther']
      },
      set (value) {
        this.$store.dispatch('setSessionCancellationReasonOther', value)
      }
    },
    sessionCancellationAutoEnabled: {
      get () {
        return this.$store.getters['getSessionCancellationAutoEnabled']
      },
      set (value) {
        this.$store.dispatch('setSessionCancellationAutoEnabled', value)
      }
    },
    sessionCancellationAutoLimit: {
      get () {
        return this.$store.getters['getSessionCancellationAutoLimit']
      }
    },
    sessionCancellationAutoLimitType: {
      get () {
        return this.$store.getters['getSessionCancellationAutoLimitType']
      }
    },
    watchContent () {
      return this.sessionCancellationSelfAllowed + this.sessionCancellationSelfLimit + this.sessionCancellationSelfLimitType +
      this.sessionCancellationReasonRequired + this.sessionCancellationReason + this.sessionCancellationReasonOther +
      this.sessionCancellationAutoEnabled + this.sessionCancellationAutoLimit + this.sessionCancellationAutoLimitType
    },
    //
    sessionAllowEdit: function () {
      return this.$store.getters.getSessionAllowEdit
    }
  },
  methods: {
    updateSessionCancellationSelfLimit (value) {
      this.$store.dispatch('setSessionCancellationSelfLimit', value)
    },
    updateSessionCancellationSelfLimitType (value) {
      this.$store.dispatch('setSessionCancellationSelfLimitType', value)
    },
    updateSessionCancellationAutoLimit (value) {
      this.$store.dispatch('setSessionCancellationAutoLimit', value)
    },
    updateSessionCancellationAutoLimitType (value) {
      this.$store.dispatch('setSessionCancellationAutoLimitType', value)
    },
    //
    sessionReset: function () {
      this.$store.dispatch('resetSession')
    },
    sessionSubmit: function () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.$route.name.includes('ebs-session-add')) {
            this.$store.dispatch('createSession')
          } else if (this.$route.name.includes('ebs-session-edit')) {
            this.$store.dispatch('updateSession')
          }
          this.allowRedirectBeforeRouteLeave()
        } else {
          this.$refs.popnotifyvalidation.openNotify()
        }
      })
    }
  },
  inject: ['$validator']
}
</script>
